<script setup lang="ts">
type EnumData = App.Base.Data.EnumData;

import type { HybridlyFormData } from '@/@types/global';

import { ButtonColor, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import FinancialAidFormFields from './FinancialAidFormFields.vue';
import Form from '@/components/form/Form.vue';
import FormFooter from '@/components/formFooter/FormFooter.vue';
import FormHeader from '@/components/formHeader/FormHeader.vue';

const props = defineProps<{
  isEditing?: boolean;
  form: HybridlyFormData<{
    status: EnumData;
    date_submitted: string;
    css_profile: EnumData;
    pell_eligible: EnumData;
    is_alternative: boolean;
    selected_for_verification: EnumData;
  }>;
  returnUrl?: string;
  studentCssProfiles: EnumData[];
  statuses: EnumData[];
  selectedForVerificationOptions: EnumData[];
  student: App.Students.Data.StudentData;
}>();

const isCancelDialogOpen = ref(false);

const { onBackForward } = useBackForward();

// TODO: Fix intecepting the back button
onBackForward(() => {
  router.abort();
});

function handleToggleCancelDialog() {
  if (props.form.isDirty) {
    isCancelDialogOpen.value = !isCancelDialogOpen.value;
    return;
  }
  handleRedirect();
}

function handleRedirect() {
  router.navigate({
    url: props.returnUrl || route('students.financial-aid.index', { student: props.student.id })
  });
}
</script>

<template>
  <Form @submit.prevent="form.submit">
    <template #header>
      <FormHeader
        canClose
        :title="`${isEditing ? 'Edit' : 'Add'} Financial Aid`"
        :description="
          isEditing
            ? 'Use the form below to edit the financial aid information.'
            : 'Fill out the form below add a new financial aid information.'
        "
        @onClose="handleToggleCancelDialog"
      />
    </template>
    <div class="divide-y-slate-200 flex flex-col gap-y-8">
      <div class="flex flex-col gap-y-8">
        <FinancialAidFormFields
          :form="form"
          :studentCssProfiles="studentCssProfiles"
          :statuses="statuses"
          :selectedForVerificationOptions="selectedForVerificationOptions"
          :student="student"
        />
      </div>
    </div>
    <template #footer>
      <FormFooter>
        <Button
          type="submit"
          :color="ButtonColor.primary"
          :isDisabled="form.processing || !form.isDirty"
          :isLoading="form.processing"
          :variant="ButtonVariant.solid"
          >Confirm</Button
        >
        <Button
          :color="ButtonColor.slate"
          :variant="ButtonVariant.outlined"
          type="button"
          @click="handleToggleCancelDialog"
        >
          Back to financial aid application
        </Button>
      </FormFooter>
    </template>
  </Form>
  <DangerDialog
    :title="`Cancel ${isEditing ? 'editing' : 'adding a new'} financial aid information`"
    :message="`Are you sure you want to cancel ${
      isEditing ? 'editing' : 'adding'
    } this financial aid information? All ${isEditing ? 'changes' : 'information'} will be lost.`"
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Go back to financial aid application"
    @onCancel="handleToggleCancelDialog"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleRedirect"
  />
</template>
